import { useState, useCallback } from "react";
import { buildOffersResponse } from '../../utils';

/**
 * Custom hook for managing duplicate offer detection and resolution
 * Handles the duplicate modal state and resolution logic
 */
const useDuplicateOffers = ({
    offersConsolidation,
    setOffersConsolidation,
    selectedDates,
    selectedStation,
    selectedDepartment,
    selectedWorkLoc,
    selectedClassification,
    selectedDutyAssig,
    setOffersSummary,
    setShowSubmitPopup,
    setShowSlotsSuccessModal,
    updateSlotsRequest
}) => {
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateOffers, setDuplicateOffers] = useState([]);
    const [duplicateResolutions, setDuplicateResolutions] = useState({});
    const [selectedOfferForSlots, setSelectedOfferForSlots] = useState(null);

    const resetDuplicateModalState = useCallback(() => {
        setShowDuplicateModal(false);
        setDuplicateOffers([]);
        setDuplicateResolutions({});
    }, []);

    const handleConfirmDuplicateResolutions = useCallback(async () => {
        try {
            const offersToCreate = [];
            const slotsToUpdate = [];
            const offerIdsToRemove = [];

            duplicateOffers.forEach((duplicate, offerIndex) => {
                const offerResolutions = duplicateResolutions[offerIndex] || {};
                let hasSlotUpdates = false;
                let hasCreateDuplicates = false;

                duplicate.duplicateDates.forEach(duplicateDate => {
                    const resolution = offerResolutions[duplicateDate.date];
                    if (resolution?.action === 'addSlots') {
                        hasSlotUpdates = true;
                        slotsToUpdate.push({
                            offerId: duplicateDate.existingOffer.offerId,
                            additionalSlots: resolution.additionalSlots,
                            currentSlots: duplicateDate.existingOffer.slots || 0,
                            date: duplicateDate.date
                        });
                    } else if (resolution?.action === 'createDuplicate') {
                        hasCreateDuplicates = true;
                    }
                });

                if (hasSlotUpdates && (hasCreateDuplicates || duplicate.nonDuplicateDates.length > 0)) {
                    const datesToCreate = [
                        ...duplicate.duplicateDates
                            .filter(dup => offerResolutions[dup.date]?.action === 'createDuplicate')
                            .map(dup => dup.date),
                        ...duplicate.nonDuplicateDates
                    ];

                    if (datesToCreate.length > 0) {
                        const modifiedOffer = {
                            ...duplicate.newOffer,
                            datesToCreate: datesToCreate
                        };
                        offersToCreate.push(modifiedOffer);
                    }
                } else if (hasSlotUpdates && !hasCreateDuplicates && duplicate.nonDuplicateDates.length === 0) {
                    offerIdsToRemove.push(duplicate.newOffer.id);
                } else if (!hasSlotUpdates) {
                    offersToCreate.push(duplicate.newOffer);
                }
            });

            let updatedOffersConsolidation = { ...offersConsolidation };
            duplicateOffers.forEach((duplicate, offerIndex) => {
                const offerResolutions = duplicateResolutions[offerIndex] || {};
                const datesWithSlotUpdates = [];

                duplicate.duplicateDates.forEach(duplicateDate => {
                    const resolution = offerResolutions[duplicateDate.date];
                    if (resolution?.action === 'addSlots') {
                        datesWithSlotUpdates.push(duplicateDate.date);
                    }
                });

                if (datesWithSlotUpdates.length > 0) {
                    const offerToUpdate = updatedOffersConsolidation[duplicate.newOffer.id];
                    if (offerToUpdate) {
                        const updatedCutOffTime = { ...offerToUpdate.cutOffTime };
                        datesWithSlotUpdates.forEach(date => {
                            delete updatedCutOffTime[date];
                        });
                        const remainingDates = selectedDates.filter(date => !datesWithSlotUpdates.includes(date));
                        if (remainingDates.length === 0) {
                            delete updatedOffersConsolidation[duplicate.newOffer.id];
                        } else {
                            updatedOffersConsolidation[duplicate.newOffer.id] = {
                                ...offerToUpdate,
                                cutOffTime: updatedCutOffTime
                            };
                        }
                    }
                }
            });

            offerIdsToRemove.forEach(offerId => {
                delete updatedOffersConsolidation[offerId];
            });

            const mergedOffersConsolidation = { ...updatedOffersConsolidation };
            offersToCreate.forEach((offerToCreate) => {
                const targetDates = offerToCreate.datesToCreate || selectedDates;
                const filteredCutOffTime = {};
                targetDates.forEach((date) => {
                    if (offerToCreate.cutOffTime?.[date]) {
                        filteredCutOffTime[date] = offerToCreate.cutOffTime[date];
                    }
                });

                mergedOffersConsolidation[offerToCreate.id] = {
                    ...offerToCreate,
                    cutOffTime: filteredCutOffTime
                };
            });

            setOffersConsolidation(mergedOffersConsolidation);
            
            if (slotsToUpdate.length > 0) {
                for (const slotUpdate of slotsToUpdate) {
                    const newSlotCount = slotUpdate.currentSlots + slotUpdate.additionalSlots;

                    try {
                        await updateSlotsRequest({
                            "offerId": slotUpdate.offerId,
                            "station": selectedStation,
                            "slots": newSlotCount
                        });
                    } catch (error) {
                        console.error(`Error updating slots for offer ${slotUpdate.offerId}:`, error);
                        alert(`Failed to update slots for offer ${slotUpdate.offerId}. Please try again.`);
                        return;
                    }
                }
            }

            if (Object.keys(mergedOffersConsolidation).length > 0) {
                const response = buildOffersResponse(
                    mergedOffersConsolidation,
                    selectedDates,
                    selectedStation,
                    selectedDepartment,
                    selectedWorkLoc,
                    selectedClassification,
                    selectedDutyAssig
                );
                setOffersSummary(response);
                resetDuplicateModalState();
                setShowSubmitPopup(true);
                return;
            }

            if (slotsToUpdate.length > 0) {
                resetDuplicateModalState();
                setShowSlotsSuccessModal(true);
            }

        } catch (error) {
            console.error('Error processing duplicate resolutions:', error);
            alert('An error occurred while processing the resolutions. Please try again.');
        }
    }, [
        duplicateOffers,
        duplicateResolutions,
        offersConsolidation,
        selectedDates,
        selectedStation,
        selectedDepartment,
        selectedWorkLoc,
        selectedClassification,
        selectedDutyAssig,
        setOffersConsolidation,
        setOffersSummary,
        setShowSubmitPopup,
        setShowSlotsSuccessModal,
        updateSlotsRequest,
        resetDuplicateModalState
    ]);

    const checkForDuplicates = useCallback((allExistingOffers) => {
        const duplicates = [];

        if (allExistingOffers && allExistingOffers.length > 0) {
            Object.values(offersConsolidation).forEach(newOffer => {
                const offerClassification = newOffer.classification || selectedClassification;
                const offerDutyAssignment = newOffer.dutyAssignment || selectedDutyAssig;
                const duplicateDates = [];

                selectedDates.forEach(date => {
                    let newOfferString = `${date} ${newOffer.offerStartTimeHr}:${newOffer.offerStartTimeMin} - ${newOffer.offerEndTimeHr}:${newOffer.offerEndTimeMin} ${offerClassification} ${offerDutyAssignment}`;

                    const matchingExistingOffer = allExistingOffers.find(existingOffer => {
                        let existingOfferString = `${existingOffer.offerStartDate} ${existingOffer.offerStartTime} - ${existingOffer.offerEndTime} ${existingOffer.classification} ${existingOffer.dutyAssignment}`;
                        return newOfferString === existingOfferString;
                    });

                    if (matchingExistingOffer) {
                        duplicateDates.push({
                            date: date,
                            existingOffer: matchingExistingOffer
                        });
                    }
                });

                if (duplicateDates.length > 0) {
                    duplicates.push({
                        newOffer: {
                            ...newOffer,
                            startTime: `${newOffer.offerStartTimeHr}:${newOffer.offerStartTimeMin}`,
                            endTime: `${newOffer.offerEndTimeHr}:${newOffer.offerEndTimeMin}`,
                            classification: offerClassification,
                            dutyAssignment: offerDutyAssignment
                        },
                        duplicateDates: duplicateDates,
                        nonDuplicateDates: selectedDates.filter(date =>
                            !duplicateDates.some(dup => dup.date === date)
                        )
                    });
                }
            });
        }

        return duplicates;
    }, [offersConsolidation, selectedDates, selectedClassification, selectedDutyAssig]);

    const initializeDuplicateResolutions = useCallback((duplicates) => {
        const initialResolutions = {};
        duplicates.forEach((duplicate, offerIndex) => {
            initialResolutions[offerIndex] = {};
            duplicate.duplicateDates.forEach((duplicateDate) => {
                initialResolutions[offerIndex][duplicateDate.date] = {
                    action: null,
                    additionalSlots: 1,
                    existingOffer: duplicateDate.existingOffer
                };
            });
        });
        return initialResolutions;
    }, []);

    return {
        // State
        showDuplicateModal,
        duplicateOffers,
        duplicateResolutions,
        selectedOfferForSlots,
        
        // Setters
        setShowDuplicateModal,
        setDuplicateOffers,
        setDuplicateResolutions,
        setSelectedOfferForSlots,
        
        // Functions
        resetDuplicateModalState,
        handleConfirmDuplicateResolutions,
        checkForDuplicates,
        initializeDuplicateResolutions
    };
};

export default useDuplicateOffers;
